/* eslint-disable react/no-array-index-key */
import * as React from 'react';

import { Sortable, Table, Tbody, Td, Th, Thead, Tr } from '@lightspeed/cirrus-table';
import { Bone } from '@lightspeed/flame/Bone';
import { Card } from '@lightspeed/flame/Card';
import { Box } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { TextLink } from '@lightspeed/flame/Text';
import isNil from 'lodash/isNil';

import { toBoolean } from 'root/apps/pages/utils/sanitize';
import Pagination from 'root/apps/PurchaseOrder/ShipmentHistory/components/Pagination';
import { ErrorMessageContext } from 'root/apps/PurchaseOrder/ShipmentHistory/contexts/ErrorMessage';
import { useOrdering } from 'root/apps/PurchaseOrder/ShipmentHistory/hooks/use-ordering';
import { OverviewDataContext } from 'root/apps/PurchaseOrder/ShipmentHistory/tabs/View/contexts/OverviewData';
import { getApiError } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/api-error-codes';
import { formatCurrency } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';
import { toArray } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/to-array';

import QuantityInput from './QuantityInput';

import type {
  APIV3OrderShipmentItem,
  OrderShipmentItem,
} from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  isMultiCurrency: boolean;
  vendorCurrencyCode?: string;
}

const PER_PAGE = 50;
const FIRST_PAGE = 1;

const ShipmentsItems: React.FC<Props> = ({ isMultiCurrency, vendorCurrencyCode }) => {
  const { isDisabled, hasPurchaseOrderLandedCostsSpreadCosts } =
    React.useContext(OverviewDataContext);
  const { setError } = React.useContext(ErrorMessageContext);
  const [data, setData] = React.useState<OrderShipmentItem[]>();
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  const initialLoadingBoneSpaces = isMultiCurrency ? 9 : 8;

  const [pagination, setPagination] = React.useState<Pagination>({
    perPage: PER_PAGE,
    currentPage: FIRST_PAGE,
  });
  const [hasProductCostPermission] = React.useState<boolean | null>(
    toBoolean(window.merchantos.session.sessData?.Rights?.product_cost)
  );
  const { orderedBy, orderDirection, getOrderDirection, setOrderDirection, getOrderParamForAPIV3 } =
    useOrdering();

  const { currency_code: currencyCode } = window.merchantos.locale;
  const systemCustomerId = window.merchantos.session.getSystemCustomerID();
  const { id: orderId, shipment_id: shipmentId } = window.history.getFilters();

  const getShipmentItems = async ({
    url,
    isPrevious,
    isNext,
    sort,
    currentPage,
  }: {
    url?: string;
    isPrevious?: boolean;
    isNext?: boolean;
    sort?: string;
    currentPage?: number;
  } = {}) => {
    setIsLoading(true);

    try {
      const res = await fetch(
        url
          ? url.replace('rad.localdev', 'localhost:3000')
          : `/API/V3/Account/${systemCustomerId}/Order/${orderId}/Shipment/${shipmentId}/Item.json?limit=${PER_PAGE}${
              sort ? `&sort=${sort}` : ''
            }`
      );

      if (res.status !== 200) {
        const { title, message } = getApiError('GENERIC_GET');
        throw new Error(title || message);
      }

      const {
        '@attributes': { previous, next },
        OrderShipmentItem: shipments,
      }: APIV3OrderShipmentItem = await res.json();

      setPagination((prev) => {
        let newCurrentPage = currentPage || prev.currentPage;
        if (isPrevious) {
          newCurrentPage = prev.currentPage - 1;
        } else if (isNext) {
          newCurrentPage = prev.currentPage + 1;
        }
        return {
          ...prev,
          previous,
          next,
          currentPage: newCurrentPage,
          direction: null,
          handlePrevious: () => {
            if (previous) {
              setPagination((prev) => ({ ...prev, direction: 'previous' }));
              getShipmentItems({ url: previous, isPrevious: true, sort });
            }
          },
          handleNext: () => {
            if (next) {
              setPagination((prev) => ({ ...prev, direction: 'next' }));
              getShipmentItems({ url: next, isNext: true, sort });
            }
          },
        };
      });
      setData(toArray(shipments));
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }

    setIsLoading(false);
    setIsInitialLoading(false);
  };

  const getShipmentItemsCount = async () => {
    try {
      const res = await fetch(
        `/API/V3/Account/${systemCustomerId}/Order/${orderId}/Shipment/${shipmentId}/Item.json?count=1`
      );

      if (res.status !== 200) {
        const { title, message } = getApiError('GENERIC_GET');
        throw new Error(title || message);
      }

      const {
        '@attributes': { count },
      } = await res.json();

      setPagination((prev) => ({ ...prev, count: Number(count) }));
    } catch (err) {
      setError(getApiError('GENERIC_GET'));
    }

    setIsLoading(false);
  };

  React.useEffect(() => {
    getShipmentItems();
    getShipmentItemsCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!orderedBy) return;

    getShipmentItems({ sort: getOrderParamForAPIV3(), currentPage: FIRST_PAGE });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderedBy, orderDirection]);

  const unitCostCalc = (cost: string, shipping: string, discount: string) => {
    return parseFloat(cost) + parseFloat(shipping) - parseFloat(discount);
  };

  const formatMoneyValueForDisplay = (value?: string, currency?: string): string => {
    return !isNil(value) ? formatCurrency(value, currency) : '-';
  };

  return (
    <Card>
      <Pagination
        isLoading={isLoading}
        isInitialLoading={isInitialLoading}
        pagination={pagination}
      />
      <Divider />
      <Table>
        <Thead>
          <Tr>
            <Sortable.Th
              disabled={!data?.length}
              direction={getOrderDirection('orderShipmentItemID')}
              onClick={() => setOrderDirection('orderShipmentItemID')}
            >
              Vendor ID
            </Sortable.Th>
            <Sortable.Th
              disabled={!data?.length}
              direction={getOrderDirection('description')}
              onClick={() => setOrderDirection('description')}
            >
              Item
            </Sortable.Th>
            <Sortable.Th
              data-test="sort-qtyReceived"
              disabled={!data?.length}
              direction={getOrderDirection('qtyReceived')}
              onClick={() => setOrderDirection('qtyReceived')}
              style={{ marginLeft: 'auto' }}
            >
              Quantity
            </Sortable.Th>

            {hasProductCostPermission ? (
              <>
                <Sortable.Th
                  disabled={!data?.length}
                  direction={getOrderDirection('vendorCost')}
                  onClick={() => setOrderDirection('vendorCost')}
                  style={{ marginLeft: 'auto' }}
                >
                  {isMultiCurrency ? (
                    <>
                      Vendor cost&nbsp;<span className="notranslate">({vendorCurrencyCode})</span>
                    </>
                  ) : (
                    <>Vendor cost</>
                  )}
                </Sortable.Th>
                {hasPurchaseOrderLandedCostsSpreadCosts ? (
                  <>
                    <Th textAlign="right">
                      Unit shipping{' '}
                      {isMultiCurrency ? (
                        <span className="notranslate">({vendorCurrencyCode})</span>
                      ) : (
                        ''
                      )}
                    </Th>
                    <Th textAlign="right">
                      Unit discount{' '}
                      {isMultiCurrency ? (
                        <span className="notranslate">({vendorCurrencyCode})</span>
                      ) : (
                        ''
                      )}
                    </Th>
                    {isMultiCurrency ? (
                      <Th textAlign="right">
                        Unit cost{' '}
                        {isMultiCurrency ? (
                          <span className="notranslate">({vendorCurrencyCode})</span>
                        ) : (
                          ''
                        )}
                      </Th>
                    ) : null}
                    <Th textAlign="right">
                      Unit cost{' '}
                      {isMultiCurrency ? <span className="notranslate">({currencyCode})</span> : ''}
                    </Th>
                  </>
                ) : null}
                {isInitialLoading ? (
                  <Th>
                    <Bone height={3} />
                  </Th>
                ) : (
                  <>
                    {isMultiCurrency && !hasPurchaseOrderLandedCostsSpreadCosts ? (
                      <Th textAlign="right">Total ({vendorCurrencyCode})</Th>
                    ) : null}
                    <Th textAlign="right">
                      Total{' '}
                      {isMultiCurrency ? <span className="notranslate">({currencyCode})</span> : ''}
                    </Th>
                  </>
                )}
              </>
            ) : null}
          </Tr>
        </Thead>
        <Tbody>
          {isLoading
            ? new Array(3).fill(0).map((_v, index) => (
                <Tr key={index}>
                  {new Array(initialLoadingBoneSpaces).fill(0).map((_x, i) => (
                    <Td key={i}>
                      <Bone height={3} width="100%" />
                    </Td>
                  ))}
                </Tr>
              ))
            : null}
          {!isLoading && data?.length
            ? data?.map((item, index) => {
                const itemLink = `/?name=item.views.item&form_name=view&id=${item.itemID}&tab=details`;
                const vendorUnitCost = unitCostCalc(
                  item.vendorCost,
                  item.shippingVendorCost,
                  item.discountMoneyVendorValue
                );
                const unitCost = unitCostCalc(
                  item.cost,
                  item.shippingCost,
                  item.discountMoneyValue
                );
                return (
                  <Tr key={item.orderShipmentItemID}>
                    <Td>{item.itemVendorID || '-'}</Td>
                    <Td>
                      <TextLink
                        data-test={`itemDescription-${index}-link`}
                        fontWeight="bold"
                        className="notranslate"
                        href={itemLink}
                        onClick={(e) => {
                          e.preventDefault();
                          window.navigateToContent(
                            'view',
                            'item.views.item',
                            item.itemID,
                            'details'
                          );
                        }}
                      >
                        {item.itemDescription}
                      </TextLink>
                    </Td>
                    <Td textAlign="right">
                      <Box width={68} display="inline-block">
                        <QuantityInput
                          value={item.qtyReceived}
                          name="qtyReceived"
                          orderId={orderId!}
                          shipmentId={item.orderShipmentID}
                          shipmentItemId={item.orderShipmentItemID}
                          isDisabled={isDisabled}
                          style={{ textAlign: 'right', borderRadius: '0.1875rem' }}
                          data-test={`qtyReceived-${index}-input`}
                          callback={(savedData) =>
                            setData((prev) =>
                              prev?.map((row) => {
                                if (
                                  Number(row.orderShipmentItemID) ===
                                  Number(savedData.orderShipmentItemID)
                                ) {
                                  return { ...row, ...savedData };
                                }
                                return row;
                              })
                            )
                          }
                        />
                      </Box>
                    </Td>
                    {hasProductCostPermission ? (
                      <>
                        {isMultiCurrency ? (
                          <Td textAlign="right">
                            <var data-test={`vendorCost-${index}-text`}>
                              {formatMoneyValueForDisplay(item.vendorCost, item.vendorCurrencyCode)}
                            </var>
                          </Td>
                        ) : (
                          <Td textAlign="right">
                            <var data-test={`cost-${index}-text`}>
                              {formatMoneyValueForDisplay(item.cost, item.currencyCode)}
                            </var>
                          </Td>
                        )}
                        {hasPurchaseOrderLandedCostsSpreadCosts ? (
                          <>
                            {isMultiCurrency ? (
                              <>
                                <Td textAlign="right">
                                  <var data-test={`unitVendorShipping-${index}-text`}>
                                    {formatMoneyValueForDisplay(
                                      item.shippingVendorCost,
                                      item.vendorCurrencyCode
                                    )}
                                  </var>
                                </Td>
                                <Td textAlign="right">
                                  <var data-test={`unitDiscount-${index}-text`}>
                                    {formatMoneyValueForDisplay(
                                      item.discountMoneyVendorValue,
                                      item.vendorCurrencyCode
                                    )}
                                  </var>
                                </Td>
                                <Td textAlign="right">
                                  <var data-test={`vendorUnitCost-${index}-text`}>
                                    {formatCurrency(vendorUnitCost, item.vendorCurrencyCode)}
                                  </var>
                                </Td>
                                <Td textAlign="right">
                                  <var data-test={`unitCost-${index}-text`}>
                                    {formatCurrency(unitCost, item.currencyCode)}
                                  </var>
                                </Td>
                              </>
                            ) : (
                              <>
                                <Td textAlign="right">
                                  <var data-test={`unitShipping-${index}-text`}>
                                    {formatMoneyValueForDisplay(
                                      item.shippingCost,
                                      item.currencyCode
                                    )}
                                  </var>
                                </Td>
                                <Td textAlign="right">
                                  <var data-test={`unitDiscount-${index}-text`}>
                                    {formatMoneyValueForDisplay(
                                      item.discountMoneyValue,
                                      item.currencyCode
                                    )}
                                  </var>
                                </Td>
                                <Td textAlign="right">
                                  <var data-test={`unitCost-${index}-text`}>
                                    {formatCurrency(unitCost, item.currencyCode)}
                                  </var>
                                </Td>
                              </>
                            )}
                          </>
                        ) : null}
                        {isMultiCurrency && !hasPurchaseOrderLandedCostsSpreadCosts ? (
                          <Td textAlign="right">
                            <var data-test={`totalVendorCost-${index}-text`}>
                              {formatMoneyValueForDisplay(
                                item.totalVendorCost,
                                item.vendorCurrencyCode
                              )}
                            </var>
                          </Td>
                        ) : null}
                        <Td textAlign="right">
                          <var data-test={`totalCost-${index}-text`}>
                            {formatMoneyValueForDisplay(item.totalCost, item.currencyCode)}
                          </var>
                        </Td>
                      </>
                    ) : null}
                  </Tr>
                );
              })
            : null}
        </Tbody>
      </Table>
      <Divider />
      <Pagination
        isLoading={isLoading}
        isInitialLoading={isInitialLoading}
        pagination={pagination}
      />
    </Card>
  );
};

export default ShipmentsItems;
